/**
 * useTypewriter - Pseudo-streaming effect for text content
 *
 * Reveals text progressively to simulate LLM streaming, even when
 * the full response is already available.
 */

import { useState, useEffect, useRef, useCallback } from 'react';

interface UseTypewriterOptions {
  /** Characters per second (default: 500 - fast but readable) */
  speed?: number;
  /** Delay before starting in ms (default: 0) */
  startDelay?: number;
  /** Whether to reveal by words instead of characters (better for markdown) */
  byWord?: boolean;
  /** Callback when typing is complete */
  onComplete?: () => void;
}

interface UseTypewriterResult {
  /** The currently visible portion of the text */
  displayedText: string;
  /** Whether the text is still being revealed */
  isTyping: boolean;
  /** Skip to the end immediately */
  skipToEnd: () => void;
  /** Reset and start typing new content */
  startTyping: (text: string) => void;
}

/**
 * Hook that provides a typewriter effect for text content.
 * Useful for simulating streaming responses from LLMs.
 *
 * @param fullText - The complete text to reveal (can change)
 * @param options - Configuration options
 * @returns Object with displayedText, isTyping state, and control functions
 */
export function useTypewriter(
  fullText: string,
  options: UseTypewriterOptions = {}
): UseTypewriterResult {
  const {
    speed = 500,
    startDelay = 0,
    byWord = true,
    onComplete,
  } = options;

  const [displayedText, setDisplayedText] = useState('');
  const [isTyping, setIsTyping] = useState(false);

  // Refs to track state without causing re-renders
  const currentIndexRef = useRef(0);
  const tokensRef = useRef<string[]>([]);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const fullTextRef = useRef(fullText);
  const onCompleteRef = useRef(onComplete);

  // Update refs when props change
  useEffect(() => {
    onCompleteRef.current = onComplete;
  }, [onComplete]);

  // Tokenize text into words or characters
  const tokenize = useCallback((text: string): string[] => {
    if (byWord) {
      // Split by word boundaries but keep whitespace attached
      // This preserves formatting better for markdown
      const tokens: string[] = [];
      let current = '';

      for (let i = 0; i < text.length; i++) {
        const char = text[i];
        current += char;

        // Word boundary: space, newline, or end of text
        if (char === ' ' || char === '\n' || i === text.length - 1) {
          if (current) {
            tokens.push(current);
            current = '';
          }
        }
      }

      return tokens;
    } else {
      // Character by character
      return text.split('');
    }
  }, [byWord]);

  // Stop any ongoing animation
  const stopAnimation = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  // Skip to end immediately
  const skipToEnd = useCallback(() => {
    stopAnimation();
    setDisplayedText(fullTextRef.current);
    setIsTyping(false);
    onCompleteRef.current?.();
  }, [stopAnimation]);

  // Start typing animation
  const startTyping = useCallback((text: string) => {
    stopAnimation();
    fullTextRef.current = text;
    tokensRef.current = tokenize(text);
    currentIndexRef.current = 0;
    setDisplayedText('');
    setIsTyping(true);

    const intervalMs = 1000 / speed;

    const startAnimation = () => {
      intervalRef.current = setInterval(() => {
        if (currentIndexRef.current >= tokensRef.current.length) {
          stopAnimation();
          setIsTyping(false);
          onCompleteRef.current?.();
          return;
        }

        // Reveal next chunk (batch a few tokens for smoother feel)
        const batchSize = byWord ? 1 : 3;
        const endIndex = Math.min(
          currentIndexRef.current + batchSize,
          tokensRef.current.length
        );

        const newText = tokensRef.current.slice(0, endIndex).join('');
        currentIndexRef.current = endIndex;
        setDisplayedText(newText);
      }, intervalMs);
    };

    if (startDelay > 0) {
      setTimeout(startAnimation, startDelay);
    } else {
      startAnimation();
    }
  }, [speed, startDelay, byWord, tokenize, stopAnimation]);

  // When fullText changes, start typing if it's new content
  useEffect(() => {
    if (fullText && fullText !== fullTextRef.current) {
      startTyping(fullText);
    } else if (fullText && !displayedText && !isTyping) {
      // Initial mount with text
      startTyping(fullText);
    }
  }, [fullText, startTyping, displayedText, isTyping]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      stopAnimation();
    };
  }, [stopAnimation]);

  return {
    displayedText,
    isTyping,
    skipToEnd,
    startTyping,
  };
}

/**
 * Simplified hook for message lists where each message needs its own typing state.
 * Returns a component wrapper that handles the typewriter effect.
 */
export function useMessageTypewriter(messageId: string, content: string, isNew: boolean) {
  const [hasAnimated, setHasAnimated] = useState(!isNew);

  const { displayedText, isTyping, skipToEnd } = useTypewriter(
    hasAnimated ? content : content,
    {
      speed: 600,
      byWord: true,
      onComplete: () => setHasAnimated(true),
    }
  );

  // If message was already shown (not new), display full text
  if (hasAnimated || !isNew) {
    return {
      displayedText: content,
      isTyping: false,
      skipToEnd: () => {},
    };
  }

  return {
    displayedText,
    isTyping,
    skipToEnd,
  };
}
